import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../Pagination";
import apiClient from "../../api/apiClient";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";

function FeedbacksList() {  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [feedbacks, setFeedbacks] = useState({
    results: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const getFeedbacks = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/api/tasks/feedback/?page=${currentPage}`);
      setFeedbacks(response.data);
      setCurrentPage(response.data.current_page);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch feedbacks on mount and whenever the page changes.
  useEffect(() => {
    getFeedbacks();
  }, [currentPage]);

  const ratingMap = {
    5: { label: "Excellent", color: "bg-green-300 text-green-800 dark:bg-green-900 dark:text-green-200" },
    4: { label: "Good", color: "bg-green-300 text-green-800 dark:bg-green-900 dark:text-green-200" },
    3: { label: "Okay", color: "bg-yellow-300 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
    2: { label: "Poor", color: "bg-red-300 text-red-800 dark:bg-red-900 dark:text-red-200" },
    1: { label: "Very Poor", color: "bg-red-300 text-red-800 dark:bg-red-900 dark:text-red-200" },
  };

  const getBadgeClass = (rating) => {
    return ratingMap[rating]?.color || "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
  };

  const getBadgeLabel = (rating) => {
    return ratingMap[rating]?.label || "No Rating";
  };

  // Navigate to the detailed feedback view.
  const handleViewClick = (feedbackId) => {
    navigate(`/feedback-view/${feedbackId}`);
    console.log(feedbackId);
  };

  // Render star ratings
  const renderStars = (rating) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <svg 
            key={i}
            className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      {isLoading ? (
        <div className="flex flex-col space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 animate-pulse">
              <div className="p-4 flex flex-col space-y-3">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/6"></div>
                </div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="flex justify-end">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/6"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {feedbacks.results.length > 0 ? (
            feedbacks.results.map((feedback) => (
              <div
                key={feedback.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">
                        Task #{feedback.task.id}
                      </span>
                      <span 
                        className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${getBadgeClass(feedback.rate)}`}
                      >
                        {getBadgeLabel(feedback.rate)} ({feedback.rate}/5)
                      </span>
                    </div>
                    <div className="flex items-center">
                      {renderStars(feedback.rate)}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Task Prompt:</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 overflow-hidden overflow-ellipsis max-h-16">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ node, ...props }) => <h1 className="text-base font-bold my-1" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-sm font-bold my-1" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-sm font-bold my-1" {...props} />,
                          h4: ({ node, ...props }) => <h4 className="text-sm font-bold my-1" {...props} />,
                          h5: ({ node, ...props }) => <h5 className="text-sm font-bold my-1" {...props} />,
                          h6: ({ node, ...props }) => <h6 className="text-sm font-bold my-1" {...props} />,
                          p: ({ node, ...props }) => <p className="my-1" {...props} />,
                          a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-1" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-1" {...props} />,
                          li: ({ node, ...props }) => <li className="my-0.5" {...props} />,
                          blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-1" {...props} />,
                          table: ({ node, ...props }) => <div className="overflow-x-auto my-1"><table className="min-w-full border border-gray-300" {...props} /></div>,
                          hr: ({ node, ...props }) => <hr className="my-2 border-t border-gray-300 dark:border-gray-700" {...props} />,
                          img: ({ node, ...props }) => <img className="max-w-full h-auto my-1 rounded" {...props} />,
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <div className="overflow-x-auto" key={`code-block-prompt-${Math.random()}`}>
                                <SyntaxHighlighter
                                  style={atomDark}
                                  showLineNumbers={true}
                                  language={match[1]}
                                  PreTag="div"
                                  {...props}
                                >
                                  {String(children).replace(/\n$/, '')}
                                </SyntaxHighlighter>
                              </div>
                            ) : (
                              <code className={`${inline ? 'px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' : ''} ${className || ''}`} {...props}>
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {feedback.task.prompt}
                      </ReactMarkdown>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Feedback:</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 overflow-hidden overflow-ellipsis max-h-16">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ node, ...props }) => <h1 className="text-base font-bold my-1" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-sm font-bold my-1" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-sm font-bold my-1" {...props} />,
                          h4: ({ node, ...props }) => <h4 className="text-sm font-bold my-1" {...props} />,
                          h5: ({ node, ...props }) => <h5 className="text-sm font-bold my-1" {...props} />,
                          h6: ({ node, ...props }) => <h6 className="text-sm font-bold my-1" {...props} />,
                          p: ({ node, ...props }) => <p className="my-1" {...props} />,
                          a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-1" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-1" {...props} />,
                          li: ({ node, ...props }) => <li className="my-0.5" {...props} />,
                          blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-1" {...props} />,
                          table: ({ node, ...props }) => <div className="overflow-x-auto my-1"><table className="min-w-full border border-gray-300" {...props} /></div>,
                          hr: ({ node, ...props }) => <hr className="my-2 border-t border-gray-300 dark:border-gray-700" {...props} />,
                          img: ({ node, ...props }) => <img className="max-w-full h-auto my-1 rounded" {...props} />,
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <div className="overflow-x-auto" key={`code-block-feedback-${Math.random()}`}>
                                <SyntaxHighlighter
                                  style={atomDark}
                                  showLineNumbers={true}
                                  language={match[1]}
                                  PreTag="div"
                                  {...props}
                                >
                                  {String(children).replace(/\n$/, '')}
                                </SyntaxHighlighter>
                              </div>
                            ) : (
                              <code className={`${inline ? 'px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' : ''} ${className || ''}`} {...props}>
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {feedback.feedback}
                      </ReactMarkdown>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleViewClick(feedback.task.id)}
                      className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gradient-to-r from-cyan-500 to-emerald-500 text-white hover:from-cyan-600 hover:to-emerald-600 transition-all duration-200"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-8">
              <div className="flex flex-col items-center justify-center text-center">
                <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"></path>
                </svg>
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">No feedbacks found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">There are no feedbacks available at the moment</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && feedbacks.results.length > 0 && (
        <div className="mt-6 flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}

export default FeedbacksList;